import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Login, Register, AuthCallback } from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Projects, { ProjectDetail } from "@/pages/Projects";
import AIReports from "@/pages/AIReports";
import CreativeStudio from "@/pages/CreativeStudio";
import { Campaigns, Vendors, RFQs } from "@/pages/Marketing";
import Leads from "@/pages/Leads";
import { SiteVisits, Inventory, Bookings, ChannelPartners, Referrals, CostOfSelling, Analytics } from "@/pages/Sales";
import { Billing, Team, Notifications, AuditLogs, Branding } from "@/pages/Org";
import LaunchWizard from "@/pages/LaunchWizard";
import Festivals from "@/pages/Festivals";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import DataDeletion from "@/pages/DataDeletion";
import ContentCalendar from "@/pages/ContentCalendar";
import ManageMetaCampaigns from "@/pages/ManageMetaCampaigns";
import SitePage from "@/pages/SitePage";
import SitePreview from "@/pages/SitePreview";
import VendorPortal from "@/pages/VendorPortal";
import Admin from "@/pages/Admin";
import Presentations from "@/pages/Presentations";
import SharedLinks from "@/pages/SharedLinks";
import PublicShare from "@/pages/PublicShare";
import AgentCompanies from "@/pages/AgentCompanies";
import AccountSettings from "@/pages/AccountSettings";
import { DomainSite } from "@/pages/SitePage";
import { useState } from "react";

const APP_HOST = new URL(process.env.REACT_APP_BACKEND_URL).hostname;
const isAppHost = (h) => h === APP_HOST || h === "localhost" || h === "127.0.0.1" || /\.(emergent\.host|emergentagent\.com|emergent\.sh)$/.test(h);

function Private({ children, superOnly }) {
  const { me, schema } = useAuth();
  if (me === null || (me && !schema)) return <div className="min-h-screen grid place-items-center bg-[#F6F7F9] text-slate-500 font-mono text-sm">Loading LaunchX360...</div>;
  if (!me) return <Navigate to="/login" replace />;
  if (superOnly && !me.is_super_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function Public({ children }) {
  const { me } = useAuth();
  if (me) return <Navigate to={me.is_super_admin ? "/admin" : me.user?.role === "marketing_agent" && !localStorage.getItem("lx_org") && (me.agent?.companies.length || 0) <= 1 ? "/agent/companies" : "/dashboard"} replace />;
  return children;
}

const AI_ROUTES = [["/advisor", "daily_advisor"], ["/intelligence/project", "project_intelligence"], ["/intelligence/buyer", "buyer_intelligence"], ["/intelligence/community", "community_intelligence"],
  ["/intelligence/corporate", "corporate_intelligence"], ["/intelligence/competitor", "competitor_intelligence"], ["/brand/positioning", "brand_positioning"],
  ["/brand/ambassadors", "brand_ambassador"], ["/marketing/planner", "marketing_plan"], ["/launch", "launch_orchestration"]];

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
          <Routes>
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/site/:slug" element={<SitePage />} />
            <Route path="/site-preview/:pid" element={<Private><SitePreview /></Private>} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/s/:token" element={<PublicShare />} />
            <Route path="/v/:token" element={<VendorPortal />} />
            <Route path="/login" element={<Public><Login /></Public>} />
            <Route path="/register" element={<Public><Register /></Public>} />
            <Route element={<Private><Layout /></Private>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              {AI_ROUTES.map(([p, t]) => <Route key={p} path={p} element={<AIReports key={t} type={t} />} />)}
              <Route path="/creative-studio" element={<CreativeStudio />} />
              <Route path="/festivals" element={<Festivals />} />
              <Route path="/content-calendar" element={<ContentCalendar />} />
              <Route path="/launch-wizard" element={<LaunchWizard />} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/ads-manager" element={<ManageMetaCampaigns />} />
              <Route path="/vendors" element={<Vendors />} />
              <Route path="/rfqs" element={<RFQs />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/site-visits" element={<SiteVisits />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/bookings" element={<Bookings />} />
              <Route path="/channel-partners" element={<ChannelPartners />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/cost-of-selling" element={<CostOfSelling />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/presentations" element={<Presentations />} />
              <Route path="/shares" element={<SharedLinks />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/branding" element={<Branding />} />
              <Route path="/team" element={<Team />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/audit-logs" element={<AuditLogs />} />
              <Route path="/agent/companies" element={<AgentCompanies />} />
              <Route path="/account" element={<AccountSettings />} />
              <Route path="/admin" element={<Private superOnly><Admin /></Private>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
  );
}

export default function App() {
  const [custom, setCustom] = useState(!isAppHost(window.location.hostname));
  if (custom) return <DomainSite onMissing={() => setCustom(false)} />;
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster theme="light" position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
